import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
import { IBasket } from "../../../models/catalogue";
import { IBasketLineAPIURL } from "../../../models/nominals";
import { loadBasket } from "./loadBasket";

/**
 * Arguments for removeBasketLine thunk.
 */
interface RemoveBasketLineArgs {
    lineURL: IBasketLineAPIURL;
}

/**
 * Remove a line from the basket.
 *
 * This thunk removes a product line from the user's basket and reloads
 * the basket to reflect updated totals and offers. Used in cart views
 * and checkout flows when removing items.
 *
 * @param {IBasketLineAPIURL} args.lineURL - API URL of the basket line to remove
 * @returns {Promise<IBasket>} Promise resolving to the reloaded basket
 *
 * @example
 * // Remove a line from basket
 * dispatch(removeBasketLine({ lineURL: line.url }));
 *
 * @sideEffects
 * - Makes API call to remove line via basketAPI.removeLine
 * - Dispatches loadBasket thunk to reload basket data
 * - Updates `data.basket` with reloaded basket via extraReducers
 * - May trigger offer recalculations on the backend
 */
export const removeBasketLine = createAsyncThunk(
    "checkout/removeBasketLine",
    async (args: RemoveBasketLineArgs, { dispatch }): Promise<IBasket> => {
        await basketAPI.removeLine(args.lineURL);
        const result = await dispatch(loadBasket()).unwrap();
        return result.basket;
    },
);
